import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/Button'
import { Field, TextInput } from '@/components/Field'
import { Kicker } from '@/components/Kicker'
import { useAppStore } from '@/stores/appStore'

type Mode = 'magic' | 'password' | 'forgot' | 'reset'

export function AuthScreen() {
  const sessionExpired = useAppStore((s) => s.sessionExpiredNotice)
  const clearExpiryNotice = useAppStore((s) => s.clearSessionExpiredNotice)

  // Supabase's password-reset redirect hits our origin with `?type=recovery` in the URL
  // fragment (PKCE flow) or query. detectSessionInUrl handles the token; we detect the
  // reset intent to render the set-new-password form instead of normal sign-in.
  const [mode, setMode] = useState<Mode>(() => {
    if (typeof window === 'undefined') return 'magic'
    const params = new URLSearchParams(window.location.search)
    if (params.get('type') === 'recovery' || window.location.hash.includes('type=recovery')) return 'reset'
    return 'password'
  })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (sessionExpired) {
      setError('Your session expired. Sign in again to continue.')
      // Clear the notice from the store so the next visit doesn't show it stale.
      const t = setTimeout(clearExpiryNotice, 100)
      return () => clearTimeout(t)
    }
  }, [sessionExpired, clearExpiryNotice])

  // NOTE: Supabase Auth's built-in verification/reset emails are HS17-safe by
  // definition — they email the account owner during verification only, never
  // clients. Distinct from the `_shared/email.ts` client-notification senders
  // that P0-Alpha correctly contained. Re-enabling these flows.
  async function sendMagicLink(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null); setMessage(null)
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: typeof window !== 'undefined' ? window.location.origin + '/' : undefined },
    })
    setBusy(false)
    if (error) { setError(error.message); return }
    setMessage('Check your email for a sign-in link.')
  }

  async function signInWithPassword(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null); setMessage(null)
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
    setBusy(false)
    if (error) setError(error.message)
  }

  async function signUp(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null); setMessage(null)
    if (password.length < 8) { setError('Password must be at least 8 characters.'); setBusy(false); return }
    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { emailRedirectTo: typeof window !== 'undefined' ? window.location.origin + '/' : undefined },
    })
    setBusy(false)
    if (error) { setError(error.message); return }
    setMessage('Account created. Check your email to confirm, then sign in.')
  }

  async function sendResetEmail(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null); setMessage(null)
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: typeof window !== 'undefined' ? window.location.origin + '/?type=recovery' : undefined,
    })
    setBusy(false)
    if (error) { setError(error.message); return }
    setMessage('If that email exists, we sent a reset link.')
  }

  async function updatePassword(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null); setMessage(null)
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); setBusy(false); return }
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    setBusy(false)
    if (error) { setError(error.message); return }
    setMessage('Password updated. Signing you in…')
    // Clean the type=recovery from URL so refresh doesn't loop.
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', window.location.origin + '/')
    }
    // Session auto-refreshes via onAuthStateChange in appStore.
  }

  return (
    <div className="min-h-svh bg-bg-base flex flex-col justify-center px-6">
      <div className="mx-auto w-full max-w-sm">
        <div className="text-center mb-10">
          <div className="font-serif italic text-display text-text-primary">Wren</div>
          <div className="mt-2 text-body text-text-tertiary">Everything that matters. In one place.</div>
        </div>

        <Kicker>
          {mode === 'magic' && 'Magic link sign-in'}
          {mode === 'password' && 'Password sign-in'}
          {mode === 'forgot' && 'Forgot password'}
          {mode === 'reset' && 'Set new password'}
        </Kicker>

        {mode === 'magic' && (
          <form onSubmit={sendMagicLink} className="mt-3 space-y-4">
            <Field label="Email">
              <TextInput
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus
              />
            </Field>
            <Button type="submit" variant="primary" size="lg" packColor="#8AB89F" loading={busy} className="w-full">
              Send magic link
            </Button>
          </form>
        )}

        {mode === 'password' && (
          <form onSubmit={signInWithPassword} className="mt-3 space-y-4">
            <Field label="Email">
              <TextInput
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field label="Password">
              <TextInput
                type="password"
                autoComplete="current-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="lg" packColor="#8AB89F" loading={busy} className="flex-1">
                Sign in
              </Button>
              <Button type="button" variant="secondary" size="lg" loading={busy} onClick={signUp}>
                Sign up
              </Button>
            </div>
            <div className="text-center">
              <button
                type="button"
                onClick={() => { setMode('forgot'); setError(null); setMessage(null) }}
                className="text-caption text-text-tertiary underline underline-offset-2 hover:text-text-secondary"
              >
                Forgot password?
              </button>
            </div>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={sendResetEmail} className="mt-3 space-y-4">
            <Field label="Email" hint="We'll email you a link to set a new password.">
              <TextInput
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus
              />
            </Field>
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="lg" packColor="#8AB89F" loading={busy} className="flex-1">
                Send reset link
              </Button>
              <Button type="button" variant="secondary" size="lg" onClick={() => { setMode('password'); setError(null); setMessage(null) }}>
                Back
              </Button>
            </div>
          </form>
        )}

        {mode === 'reset' && (
          <form onSubmit={updatePassword} className="mt-3 space-y-4">
            <div className="text-caption text-text-secondary">
              You clicked the password-reset link. Choose a new password — you'll stay signed in after saving.
            </div>
            <Field label="New password" hint="At least 8 characters.">
              <TextInput
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
              />
            </Field>
            <Button type="submit" variant="primary" size="lg" packColor="#8AB89F" loading={busy} className="w-full">
              Set new password
            </Button>
          </form>
        )}

        {message && <div className="mt-4 rounded-r-sm bg-pack-early-care/15 border border-pack-early-care/30 p-3 text-caption text-pack-early-care">{message}</div>}
        {error && <div className="mt-4 rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">{error}</div>}

        {(mode === 'magic' || mode === 'password') && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => { setMode(mode === 'magic' ? 'password' : 'magic'); setError(null); setMessage(null) }}
              className="text-caption text-text-tertiary underline underline-offset-2 hover:text-text-secondary"
            >
              {mode === 'magic' ? 'Use email + password instead' : 'Use magic link instead'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
